import { DebridSettings } from '../components/DebridSettings';
import { useEffect, useMemo, useState } from 'react';
import { ApiError, call, iptv, metaApi, onboardingApi, syncApi, type IptvAccount, type InviteAddon, type Me, type Policy } from '../api';
import { addonsApi, refreshAddons } from '../addons';
import { AddonSettings } from '../components/AddonSettings';
import { SimklLink, TraktLink } from '../components/Connections';
import { SyncSettings } from '../components/SyncSettings';
import { PlaybackSettings } from '../components/PlaybackSettings';
import { SportCenterSettings } from '../components/SportCenterSettings';
import { ThemePicker } from '../components/ThemePicker';
import { loadPrefs, prefsNow, savePrefs } from '../prefs';
import { AddAccount } from './Settings';

/**
 * Welkomst-wizard voor wie zich via een uitnodiging heeft aangemeld (ook te openen via /welkom).
 * Elke stap is over te slaan; alles staat daarna ook gewoon bij Instellingen.
 */

type Use = 'live' | 'sport' | 'music';
type StepId = 'welcome' | 'choose' | 'live' | 'addons' | 'debrid' | 'music' | 'sport' | 'history' | 'language' | 'start' | 'look' | 'done';

const STEP_LABEL: Record<StepId, string> = {
  welcome: 'Welkom',
  choose: 'Wat wil je gebruiken?',
  live: 'Live TV',
  addons: 'Extra bronnen',
  debrid: 'Debrid koppelen',
  music: 'Muziek',
  sport: 'Sport Center',
  history: 'Kijkgeschiedenis',
  language: 'Taal',
  start: 'Je startscherm',
  look: 'Uiterlijk',
  done: 'Klaar',
};

export function Onboarding({ me, onDone }: { me: Me; onDone: () => void }) {
  const [info, setInfo] = useState<{ name: string; offeredAddons: InviteAddon[] } | null>(null);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [uses, setUses] = useState<Set<Use>>(new Set(['live', 'sport', 'music']));
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [syncable, setSyncable] = useState(false);

  useEffect(() => {
    onboardingApi.info().then(setInfo).catch(() => setInfo({ name: me.profile?.title ?? me.account.title, offeredAddons: [] }));
    call<{ policy: Policy }>('/api/policy').then((r) => setPolicy(r.policy)).catch(() => setPolicy(null));
    void loadPrefs(true);
    syncApi.status().then((r) => setSyncable(r.trakt.configured || r.simkl.configured)).catch(() => setSyncable(false));
  }, [me]);

  const allowed = useMemo(() => ({
    live: policy?.liveTv !== false,
    music: policy?.music !== false,
    addons: policy?.addons !== false,
    debrid: policy?.addons !== false && policy?.debrid === true,
    sport: true,
  }), [policy]);

  const steps = useMemo<StepId[]>(() => {
    const s: StepId[] = ['welcome', 'choose'];
    if (allowed.live && uses.has('live')) s.push('live');
    if (allowed.addons) s.push('addons');
    if (allowed.debrid) s.push('debrid');
    if (allowed.music && uses.has('music')) s.push('music');
    if (uses.has('sport')) s.push('sport');
    if (syncable) s.push('history');
    s.push('language', 'start', 'done');
    return s;
  }, [allowed, uses, syncable]);

  const id = steps[Math.min(step, steps.length - 1)];
  const next = () => setStep((i) => Math.min(i + 1, steps.length - 1));
  const back = () => setStep((i) => Math.max(i - 1, 0));

  useEffect(() => {
    document.querySelector('.ob-body')?.scrollTo({ top: 0 });
  }, [id]);

  const toggle = (u: Use) => setUses((prev) => {
    const n = new Set(prev);
    if (n.has(u)) n.delete(u); else n.add(u);
    return n;
  });

  const finish = async () => {
    setBusy(true);
    try {
      // Wat je niet gebruikt verdwijnt uit het menu (later terug te zetten bij Uiterlijk → Menu)
      const nav = prefsNow().nav ?? { order: [], hidden: [] };
      const hidden = new Set(nav.hidden);
      for (const [u, navId] of [['live', 'live'], ['sport', 'sport'], ['music', 'music']] as const) {
        if (!allowed[u]) continue; // niet toegestaan: dat regelt de beheerder, niet het menu
        if (uses.has(u)) hidden.delete(navId); else hidden.add(navId);
      }
      await savePrefs({ nav: { order: nav.order, hidden: [...hidden] } }).catch(() => undefined);
      await onboardingApi.done().catch(() => undefined);
      refreshAddons();
    } finally {
      setBusy(false);
      onDone();
    }
  };

  const name = info?.name ?? me.profile?.title ?? '';

  return (
    <main className="ob-screen ob-wizard">
      <header className="ob-head">
        <div className="logo">tu<span>media</span></div>
        <ol className="ob-steps" aria-label="Stappen">
          {steps.map((s, i) => (
            <li key={s} className={i === step ? 'is-now' : i < step ? 'is-done' : undefined} aria-current={i === step ? 'step' : undefined}>
              <span>{STEP_LABEL[s]}</span>
            </li>
          ))}
        </ol>
      </header>

      <div className="ob-body" key={id}>
        {id === 'welcome' && (
          <section className="ob-hero">
            <h1 className="ob-title">Welkom{name ? `, ${name}` : ''}!</h1>
            <p className="lead">Fijn dat je er bent. In een paar stappen stellen we tumedia voor je in: wat je wilt kijken en luisteren, je clubs, je taal en hoe het eruitziet.</p>
            <p className="subtle">Alles is over te slaan en kun je later aanpassen bij Instellingen.</p>
          </section>
        )}

        {id === 'choose' && (
          <section>
            <h1 className="ob-title">Wat wil je gebruiken?</h1>
            <p className="muted">Films en series uit de bibliotheek heb je altijd. Kies wat je er nog meer bij wilt.</p>
            <div className="ob-tiles" role="group" aria-label="Onderdelen">
              <button type="button" className="ob-tile is-on is-fixed" aria-pressed="true" disabled>
                <span className="ob-tile-icon" aria-hidden="true">🎬</span><b>Films &amp; series</b><small>{me.plex === false ? 'Via je addons' : 'Altijd aan'}</small>
              </button>
              {allowed.live && (
                <button type="button" className={`ob-tile${uses.has('live') ? ' is-on' : ''}`} aria-pressed={uses.has('live')} onClick={() => toggle('live')}>
                  <span className="ob-tile-icon" aria-hidden="true">📺</span><b>Live TV</b><small>Met je eigen IPTV-abonnement</small>
                </button>
              )}
              <button type="button" className={`ob-tile${uses.has('sport') ? ' is-on' : ''}`} aria-pressed={uses.has('sport')} onClick={() => toggle('sport')}>
                <span className="ob-tile-icon" aria-hidden="true">⚽</span><b>Sport Center</b><small>Uitslagen, standen en je clubs</small>
              </button>
              {allowed.music && (
                <button type="button" className={`ob-tile${uses.has('music') ? ' is-on' : ''}`} aria-pressed={uses.has('music')} onClick={() => toggle('music')}>
                  <span className="ob-tile-icon" aria-hidden="true">🎵</span><b>Muziek</b><small>{me.plex === false ? 'Met Eclipse-addons' : 'Plex-muziek en eventueel addons'}</small>
                </button>
              )}
            </div>
          </section>
        )}

        {id === 'live' && <LiveStep />}

        {id === 'addons' && <AddonsStep offered={info?.offeredAddons ?? []} />}

        {id === 'debrid' && (
          <div className="ob-debrid">
            <p className="subtle">Heb je een TorBox- of Real-Debrid-account? Koppel hem hier, dan kun je films en series meteen afspelen via je eigen internetverbinding. Geen account? Sla deze stap gerust over; het kan later bij Instellingen → Debrid.</p>
            <DebridSettings />
          </div>
        )}

        {id === 'music' && <MusicStep plex={me.plex !== false} />}

        {id === 'sport' && (
          <section>
            <h1 className="ob-title">Sport Center</h1>
            <p className="muted">Kies je competities en clubs. Die zie je terug in het Sport Center, bij Live TV en in de kalender.</p>
            <SportCenterSettings inSettings />
          </section>
        )}

        {id === 'history' && <HistoryStep />}

        {id === 'language' && (
          <section>
            <h1 className="ob-title">Taal</h1>
            <p className="muted">tumedia kiest automatisch het goede audio- en ondertitelspoor, bijvoorbeeld Engels gesproken met Nederlandse ondertitels.</p>
            <PlaybackSettings title="Audio en ondertitels" groups={['audio', 'subs']} intro={false} />
          </section>
        )}

        {id === 'start' && <StartStep />}

        {id === 'look' && (
          <section>
            <h1 className="ob-title">Uiterlijk</h1>
            <p className="muted">De Netflix-look staat standaard aan: kaarten die openklappen met een trailer en een groot billboard bovenaan.</p>
            <PlaybackSettings title="Netflix-look" groups={['netflix']} intro={false} />
            <ThemePicker />
          </section>
        )}

        {id === 'done' && (
          <section className="ob-hero">
            <h1 className="ob-title">Klaar!</h1>
            <p className="lead">Alles staat voor je klaar{name ? `, ${name}` : ''}.</p>
            <ul className="ob-summary">
              <li>🎬 Films &amp; series</li>
              {allowed.live && uses.has('live') && <li>📺 Live TV</li>}
              {uses.has('sport') && <li>⚽ Sport Center</li>}
              {allowed.music && uses.has('music') && <li>🎵 Muziek</li>}
            </ul>
            <p className="subtle">Tip: op je iPhone kun je tumedia via Delen → <i>Zet op beginscherm</i> als app gebruiken. Voor de iPhone- en Apple TV-app vraag je de beheerder.</p>
          </section>
        )}
      </div>

      <footer className="ob-foot">
        {step > 0 && id !== 'done' ? <button type="button" className="btn btn-ghost" onClick={back}>Terug</button> : <span />}
        {id === 'done' ? (
          <button type="button" className="btn btn-primary" onClick={() => void finish()} disabled={busy} data-autofocus>{busy ? 'Even geduld…' : 'Begin met kijken'}</button>
        ) : (
          <div className="ob-foot-r">
            {id !== 'welcome' && id !== 'choose' && <button type="button" className="btn btn-ghost" onClick={next}>Overslaan</button>}
            <button type="button" className="btn btn-primary" onClick={next} data-autofocus>{id === 'welcome' ? 'Beginnen' : 'Volgende'}</button>
          </div>
        )}
      </footer>
    </main>
  );
}

function LiveStep() {
  const [accounts, setAccounts] = useState<IptvAccount[] | null>(null);
  const [adding, setAdding] = useState(false);
  const load = () => iptv.accounts().then((r) => setAccounts(r.accounts)).catch(() => setAccounts([]));
  useEffect(() => {
    void load();
  }, []);
  return (
    <section>
      <h1 className="ob-title">Live TV</h1>
      <p className="muted">Voeg je eigen IPTV-abonnement toe (Xtream). tumedia controleert meteen of het werkt. Je wachtwoord wordt versleuteld bewaard en is daarna niet meer zichtbaar, ook niet voor de beheerder.</p>
      {accounts?.map((a) => (
        <div key={a.id} className="ob-ok">
          <b>✓ {a.name}</b>
          <span className="subtle">{a.status ?? 'Toegevoegd'}{a.expiresAt ? ` · geldig tot ${new Date(a.expiresAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''} · {a.maxConnections} {a.maxConnections === 1 ? 'stream' : 'streams'} tegelijk</span>
        </div>
      ))}
      {accounts && (accounts.length === 0 || adding) ? (
        <AddAccount onDone={() => { setAdding(false); void load(); }} onCancel={accounts.length ? () => setAdding(false) : undefined} />
      ) : accounts ? (
        <button type="button" className="btn" onClick={() => setAdding(true)}>Nog een account toevoegen</button>
      ) : (
        <p className="subtle">Laden…</p>
      )}
    </section>
  );
}

function AddonsStep({ offered }: { offered: InviteAddon[] }) {
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [own, setOwn] = useState(false);
  const add = async (a: InviteAddon) => {
    setError(null);
    try {
      await onboardingApi.addAddon(a.id);
      setAdded((s) => new Set(s).add(a.id));
      refreshAddons();
    } catch {
      setError(`${a.name} toevoegen mislukt.`);
    }
  };
  return (
    <section>
      <h1 className="ob-title">Extra bronnen</h1>
      <p className="muted">Addons voegen bronnen toe voor films, series, sport en muziek{offered.length ? '. De beheerder heeft er een paar voor je klaargezet' : ''}.</p>
      {offered.length > 0 && (
        <div className="ob-offers">
          {offered.map((a) => (a.mode === 'recommend'
            ? <RecommendedAddon key={a.id} a={a} />
            : (
              <div key={a.id} className="ob-offer">
                <AddonIcon a={a} />
                <div>
                  <b>{a.name}</b>
                  <small>{a.kind === 'music' ? 'Muziek' : 'Video'}{a.description ? ` · ${a.description}` : ''}</small>
                </div>
                {added.has(a.id)
                  ? <span className="ob-added">✓ Toegevoegd</span>
                  : <button type="button" className="btn btn-sm btn-primary" onClick={() => void add(a)}>Toevoegen</button>}
              </div>
            )))}
        </div>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      {own ? (
        <AddonSettings />
      ) : (
        <button type="button" className="btn" onClick={() => setOwn(true)}>Zelf een addon toevoegen</button>
      )}
    </section>
  );
}

function AddonIcon({ a }: { a: InviteAddon }) {
  return a.icon ? <img src={a.icon} alt="" /> : <span className="ob-offer-i" aria-hidden="true">{a.kind === 'music' ? '🎵' : '🧩'}</span>;
}

/** Aanrader: zelf instellen met je eigen sleutels (Real-Debrid, TorBox…), daarna het adres plakken. */
function RecommendedAddon({ a }: { a: InviteAddon }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await addonsApi.add(url.trim());
      setDone(r.addon.name);
      refreshAddons();
    } catch (err) {
      setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Dit adres werkt niet. Kopieer het volledige adres (eindigt op manifest.json).');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ob-offer ob-reco">
      <div className="ob-reco-top">
        <AddonIcon a={a} />
        <div>
          <b>{a.name} <span className="ob-tag">Aanrader</span></b>
          <small>{a.kind === 'music' ? 'Muziek' : 'Video'} · stel hem in met je eigen account{a.description ? ` · ${a.description}` : ''}</small>
        </div>
        {done && <span className="ob-added">✓ Toegevoegd</span>}
      </div>
      {!done && (
        <>
          <ol className="ob-reco-steps">
            <li>
              Open de instelpagina en vul daar je <b>eigen</b> gegevens in (bijvoorbeeld je Real-Debrid- of TorBox-sleutel).
              {a.setupUrl && <> <a className="btn btn-sm" href={a.setupUrl} target="_blank" rel="noreferrer">Instellen ↗</a></>}
            </li>
            <li>Kopieer daar het installatie-adres (eindigt op <code>manifest.json</code>) en plak het hieronder.</li>
          </ol>
          <form className="ob-reco-form" onSubmit={(e) => { e.preventDefault(); void add(); }}>
            <input className="text-input" type="url" required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/manifest.json" aria-label={`Adres van ${a.name}`} autoComplete="off" />
            <button className="btn btn-sm btn-primary" type="submit" disabled={busy || !url.trim()}>{busy ? 'Controleren…' : 'Toevoegen'}</button>
          </form>
          {error && <p className="error" role="alert">{error}</p>}
        </>
      )}
    </div>
  );
}

function HistoryStep() {
  const [n, setN] = useState(0);
  return (
    <section>
      <h1 className="ob-title">Kijkgeschiedenis</h1>
      <p className="muted">Houd je bij wat je kijkt op Trakt of SIMKL? Koppel je eigen account; tumedia werkt het dan vanzelf bij. Je kunt ook je bestaande geschiedenis overzetten. Geen account? Sla deze stap gewoon over.</p>
      <TraktLink onLinked={() => void savePrefs({ traktScrobble: true }).then(() => setN((x) => x + 1))} />
      <SimklLink onLinked={() => void savePrefs({ simklSync: true }).then(() => setN((x) => x + 1))} />
      <SyncSettings refreshKey={n} />
    </section>
  );
}

function MusicStep({ plex = true }: { plex?: boolean }) {
  const [eclipse, setEclipse] = useState<boolean | null>(null);
  return (
    <section>
      <h1 className="ob-title">Muziek</h1>
      <p className="muted">{plex ? 'Muziek uit de Plex-bibliotheek staat voor je aan, tot in Hi-Res.' : 'Zonder Plex komt muziek uit Eclipse-addons.'}</p>
      <div className="ob-question">
        <b>Wil je ook Eclipse-addons gebruiken voor muziek?</b>
        <div className="form-actions">
          <button type="button" className={`btn${eclipse === true ? ' btn-primary' : ''}`} aria-pressed={eclipse === true} onClick={() => setEclipse(true)}>Ja</button>
          <button type="button" className={`btn${eclipse === false ? ' btn-primary' : ''}`} aria-pressed={eclipse === false} onClick={() => setEclipse(false)}>{plex ? 'Nee, alleen Plex' : 'Nee, geen muziek'}</button>
        </div>
      </div>
      {eclipse && (
        <>
          <p className="subtle">Plak het adres van je Eclipse-addon (eindigt meestal op <code>manifest.json</code>) of open een addon-winkel. Je persoonlijke gegevens in het adres worden versleuteld bewaard.</p>
          <AddonSettings />
        </>
      )}
    </section>
  );
}

/** Startscherm kiezen: Standaard (door de beheerder bepaald) of Netflix-look. Overslaan = Standaard. */
function StartStep() {
  const [choice, setChoice] = useState<'standard' | 'netflix' | null>(null);
  const [busy, setBusy] = useState(false);
  const [limits, setLimits] = useState<{ tmdb: boolean; personal: boolean } | null>(null);
  useEffect(() => {
    Promise.all([
      metaApi.providers().then((r) => !!r.providers.tmdb?.configured).catch(() => true),
      syncApi.status().then((r) => r.trakt.linked || r.simkl.linked).catch(() => false),
    ]).then(([tmdb, personal]) => setLimits({ tmdb, personal }));
  }, []);
  const pick = async (c: 'standard' | 'netflix') => {
    setBusy(true);
    try {
      await call('/api/onboarding/layout', { method: 'POST', body: JSON.stringify({ choice: c }) });
      setChoice(c);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      <h1 className="ob-title">Je startscherm</h1>
      <p className="muted">Kies hoe je startscherm eruitziet. Later kun je alles zelf aanpassen bij <b>Instellingen → Uiterlijk</b>: rijen (widgets) toevoegen of weghalen, eigen pagina's maken en een andere skin of kleuren kiezen.</p>
      <div className="ob-tiles" data-focus-group>
        <button type="button" className={`ob-tile ob-start${choice === 'standard' ? ' is-on' : ''}`} aria-pressed={choice === 'standard'} disabled={busy} onClick={() => void pick('standard')} data-autofocus>
          <span className="ob-tile-icon" aria-hidden="true">🏠</span>
          <b>Standaard</b>
          <small>Eenvoudig: Home met trending films en series, plus aparte pagina's voor Films en Series.</small>
        </button>
        <button type="button" className={`ob-tile ob-start${choice === 'netflix' ? ' is-on' : ''}`} aria-pressed={choice === 'netflix'} disabled={busy} onClick={() => void pick('netflix')}>
          <span className="ob-tile-icon" aria-hidden="true">🍿</span>
          <b>Netflix-look</b>
          <small>Groot billboard bovenaan, Top 10's en veel rijen op één pagina.</small>
        </button>
      </div>
      {choice && <p className="subtle" role="status">✓ {choice === 'netflix' ? 'Netflix-look' : 'Standaard'} gekozen. Klik op Volgende.</p>}
      {!choice && <p className="subtle">Geen keuze? Klik op Overslaan, dan krijg je Standaard.</p>}
      {limits && (!limits.tmdb || !limits.personal) && (
        <div className="ob-note" role="note">
          <b>Goed om te weten</b>
          {!limits.tmdb ? (
            <p>Op deze server is TMDB nog niet gekoppeld. Daardoor blijven rijen zoals Trending en Top 10 leeg. Vraag de beheerder om dit in te stellen.</p>
          ) : (
            <p>Rijen zoals Trending en Top 10 werken meteen. Persoonlijke rijen (je watchlist, aanbevelingen, "omdat je … keek") en eigen lijsten werken pas als je <b>Trakt</b> of <b>SIMKL</b> koppelt. Dat kan bij de stap Kijkgeschiedenis of later bij Instellingen → Koppelingen.</p>
          )}
        </div>
      )}
    </section>
  );
}
